<template>
  <FriedModal @close="$emit('close')">
    <template #header>
      <div class="carousel-header">
        <LinkButton is-bold :text="t('cc_view_all_button')" @click="$emit('view-all')" />
        <CarouselControls
          class="carousel-controls"
          :current-comment-index="currentCommentIndex"
          :total-comments-count="comments.length"
          @next="move(1)"
          @previous="move(-1)"
        />
        <div />
      </div>
      <CommentTargetText
        class="comment-target-text"
        :target-text="currentComment.editorSelection.content"
        :is-resolved="isResolved"
        :is-ghost="isGhost"
      />
    </template>
    <FriedCard v-if="!isDraft" class="comment-container">
      <MessageContainer
        :class="{ 'is-resolved': isResolved }"
        :created-at="currentComment.createdAt || 0"
        :is-read="isCommentRead(currentComment, currentUser)"
        :is-resolvable="isResolvable"
        :read-only="readOnly"
        :created-by-meta-data="getUserMetaData(currentComment.createdBy, recipients, sender)"
        :text="currentComment.richText || ''"
        :expanded="replyEngaged"
        :read-events="mappedLatestReadEvents[currentComment.id]"
        @resolve="resolve"
      />
      <div v-for="reply in visibleReplies" :key="reply.id">
        <div class="message-divider"></div>
        <MessageContainer
          :class="{ 'is-resolved': isResolved }"
          :data-external="`conversation-card-replyId:${reply.id}-reply-container`"
          :created-at="reply.createdAt || 0"
          :is-read="isRead(reply.reads, currentUser)"
          :is-reply="true"
          :current-user="currentUser"
          :read-only="readOnly"
          :text="reply.richText || ''"
          :created-by-meta-data="getUserMetaData(reply.createdBy, recipients, sender)"
          :expanded="replyEngaged"
          :read-events="mappedLatestReadEvents[reply.id]"
        />
      </div>
      <template v-if="replyEngaged">
        <div class="pulsing-line-divider"></div>
        <PulsingLine class="pulsing-line" />
      </template>
    </FriedCard>
    <template #footer>
      <FriedModalFooter class="carousel-footer">
        <CommentInput
          v-if="isDraft || replyEngaged"
          :data-external="`comment-card-id:${currentComment.id}-input:`"
          :current-user="currentUser"
          :value="richTextValue"
          :selected="true"
          :placeholder="t('cc_input_field_type_comment_here')"
          @input="$event => isDraft && emitTextDebounce($event)"
          @submit="handleSubmit"
          @discard="cancelComment"
        />
        <FriedButton
          v-else
          class="reply-to-thread-button"
          data-external="contextual-commenting-reply-to-thread-button"
          :button-type="ButtonType.Secondary"
          @click="replyEngaged = true"
          >{{ t('cc_reply_to_thread') }}
        </FriedButton>
      </FriedModalFooter>
    </template>
  </FriedModal>
</template>

<script lang="ts">
import { useI18n } from 'vue-i18n';
import { ButtonType } from '@getaccept/fried-tofu';
import type { PropType } from 'vue';
import { computed, defineComponent, ref, toRefs, watch, nextTick } from 'vue';
import { useDebounceFn } from '@vueuse/core';
import type { Recipient } from '../../../types/Recipient';
import type { User } from '../../../users/types/user';
import LinkButton from '../../components/LinkButton.vue';
import { useReadEvents } from '../../composables/read-events.composable';
import CommentInput from '../../components/CommentInput.vue';
import CommentTargetText from '../../components/CommentTargetText.vue';
import MessageContainer from '../../components/MessageContainer.vue';
import { CommentsHelper } from '../../helpers/comments.helper';
import type { Comment } from '../../types/comment';
import { CommentStatus } from '../../types/comment-status';
import type { CurrentUser } from '../../types/current-user';
import type { UpdateCommentDto } from '../../types/update-comment-dto';
import { useCommentMetadata } from '../../composables/comment-metadata.composable';
import PulsingLine from './PulsingLine.vue';
import CarouselControls from './CarouselControls.vue';

export default defineComponent({
  components: {
    LinkButton,
    CarouselControls,
    CommentTargetText,
    MessageContainer,
    CommentInput,
    PulsingLine,
  },
  props: {
    comments: {
      type: Array as PropType<Comment[]>,
      default: () => [],
    },
    currentUser: {
      type: Object as PropType<CurrentUser>,
      required: true,
    },
    selectedId: {
      type: String,
      default: '',
    },
    recipients: {
      type: Array as PropType<Recipient[]>,
      required: true,
    },
    sender: {
      type: Object as PropType<User>,
      required: true,
    },
    readOnly: {
      type: Boolean,
    },
  },
  emits: ['view-all', 'close', 'update', 'create-reply', 'delete', 'select'],
  setup(props, { emit }) {
    const { t } = useI18n();
    const { comments, currentUser, selectedId, recipients, sender, readOnly } = toRefs(props);

    const currentCommentIndex = ref(comments.value.findIndex(({ id }) => id === selectedId.value));
    const currentComment = computed(() => comments.value[currentCommentIndex.value]);

    const replyEngaged = ref(false);

    const { isDraft, isResolved, isGhost, isResolvable, richTextValue, visibleReplies } =
      useCommentMetadata(currentComment, currentUser, readOnly, replyEngaged);

    const move = (offset: number) => {
      const newIndex = currentCommentIndex.value + offset;
      currentCommentIndex.value =
        newIndex < 0 ? comments.value.length - 1 : newIndex >= comments.value.length ? 0 : newIndex;

      emit('select', currentComment.value.id);
    };

    watch(
      () => selectedId.value,
      id => {
        const index = comments.value.findIndex(comment => comment.id === id);
        currentCommentIndex.value = index === -1 ? 0 : index;
      },
      { immediate: true }
    );

    const resolve = () => {
      const payload: Partial<UpdateCommentDto> = {
        commentId: currentComment.value.id,
        status: CommentStatus.Resolved,
      };
      emit('update', payload);
    };

    const emitText = ({ richText, text }: { richText: string; text: string }) => {
      const payload: Partial<UpdateCommentDto> = {
        richText,
        text,
        commentId: currentComment.value.id,
      };
      emit('update', payload);
    };

    const emitTextDebounce = useDebounceFn(emitText, 300);

    const handleSubmit = ({ richText, text }: { richText: string; text: string }) => {
      if (isDraft.value) {
        const payload: Partial<UpdateCommentDto> = {
          commentId: currentComment.value.id,
          richText,
          text,
          status: CommentStatus.Active,
        };
        emit('update', payload);
      } else {
        emit('create-reply', { commentId: currentComment.value.id, richText, text });
      }
    };

    const cancelComment = async () => {
      if (isDraft.value) {
        emit('delete', selectedId.value);
        await nextTick();
        move(-1);
        return;
      }
      replyEngaged.value = false;
    };

    const { mappedLatestReadEvents } = useReadEvents(
      currentComment,
      recipients.value,
      sender.value,
      currentUser
    );

    return {
      ButtonType,

      currentComment,
      currentCommentIndex,
      visibleReplies,
      mappedLatestReadEvents,

      isDraft,
      isResolved,
      isGhost,
      isResolvable,
      replyEngaged,
      richTextValue,

      t,
      move,
      isRead: CommentsHelper.isRead,
      isCommentRead: CommentsHelper.isCommentRead,
      getUserMetaData: CommentsHelper.getUserMetaData,
      resolve,
      handleSubmit,
      cancelComment,
      emitTextDebounce,
    };
  },
});
</script>
<style lang="scss" scoped>
$close-button-width: 40px;

.carousel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.comment-target-text {
  margin-right: -$close-button-width;
  margin-top: var(--spacing-75);
}

.comment-container {
  padding: var(--spacing-75) var(--spacing-100) var(--spacing-100) var(--spacing-100);
  box-shadow: none;
  outline: 2px solid var(--warning-dark);
  outline-offset: -2px;
  overflow: scroll;

  .message-divider {
    height: 1px;
    width: 100%;
    background-color: var(--gray-96);
    margin: var(--spacing-50) 0;
  }

  .pulsing-line-divider {
    height: 1px;
    width: 100%;
    background-color: var(--gray-96);
    margin: var(--spacing-100) 0;
  }

  .pulsing-line {
    margin: var(--spacing-75) 0 var(--spacing-50) 0;
  }
}

.carousel-footer {
  .reply-to-thread-button {
    width: 100%;
  }
}
</style>
